import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Inbox from '../components/Inbox';
import DisputeDialog from '../components/DisputeDialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Clock, CheckCircle, XCircle, 
  Package, MessageSquare, User, DollarSign, TrendingUp, Wallet,
  Settings, ArrowDownToLine, Music,
  MessageCircle, ChevronDown, ChevronUp, Handshake as HandshakeIcon, Inbox as InboxIcon,
  AlertTriangle, Bell, BadgeCheck, FolderDown, Mic2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { OrdersTab, OffersTab, EarningsTab, DeliverDialog, WithdrawDialog, CounterOfferDialog, NotificationsPanel, ProfileEditDialog, WithdrawalsTab } from '../components/artist-dashboard';
import { ExportsPanel } from '../components/studio/ExportsPanel';
import { Link } from 'react-router-dom';

const statusConfig = {
  pending_payment: { label: 'Pending Payment', color: 'bg-yellow-500/10 text-yellow-500' },
  paid: { label: 'Awaiting Response', color: 'bg-blue-500/10 text-blue-500' },
  accepted: { label: 'In Progress', color: 'bg-purple-500/10 text-purple-500' },
  denied: { label: 'Denied', color: 'bg-red-500/10 text-red-500' },
  delivered: { label: 'Delivered', color: 'bg-lime/10 text-lime' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500/10 text-gray-500' },
  refunded: { label: 'Refunded', color: 'bg-orange-500/10 text-orange-500' },
};

const offerStatusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/10 text-yellow-500', icon: Clock },
  countered: { label: 'Counter Sent', color: 'bg-purple-500/10 text-purple-500', icon: HandshakeIcon },
  accepted: { label: 'Accepted', color: 'bg-lime/10 text-lime', icon: CheckCircle },
  denied: { label: 'Denied', color: 'bg-red-500/10 text-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-500/10 text-gray-500', icon: Clock },
  completed: { label: 'Paid', color: 'bg-green-500/10 text-green-500', icon: CheckCircle },
};

export default function ArtistDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [profile, setProfile] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [offers, setOffers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [studioExports, setStudioExports] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'inbox');
  const deleteTimerRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const fileInputRef = useRef(null);
  
  // Counter offer dialog state
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [processingOfferId, setProcessingOfferId] = useState(null);

  // Dispute dialog state
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedOrderForDispute, setSelectedOrderForDispute] = useState(null);

  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentData, setPaymentData] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);

  // Available genres
  const handleGenreToggle = (genre) => {
    setProfileForm(prev => ({
      ...prev,
      genres: (prev.genres || []).includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...(prev.genres || []), genre]
    }));
  };

  const loadStudioExports = useCallback(async () => {
    try { setStudioExports((await api.getStudioExports()).data); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { loadStudioExports(); }, [loadStudioExports]);

  // Intentionally [] — api/toast/setters are stable module-level or useState refs
  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, earningsRes, profileRes, withdrawalsRes, offersRes, inboxRes, notifsRes] = await Promise.all([
        api.getArtistOrders(),
        api.getArtistEarnings().catch(() => ({ data: null })),
        api.getArtistProfile().catch(() => ({ data: null })),
        api.getArtistWithdrawals().catch(() => ({ data: [] })),
        api.getArtistOffers().catch(() => ({ data: [] })),
        api.getInboxUnreadCount().catch(() => ({ data: { unread_count: 0 } })),
        api.getNotifications().catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data);
      setEarnings(earningsRes.data);
      setProfile(profileRes.data);
      setWithdrawals(withdrawalsRes.data || []);
      setOffers(offersRes.data || []);
      setInboxUnread(inboxRes.data?.unread_count || 0);
      setNotifications(notifsRes.data || []);
      window.dispatchEvent(new Event('refreshBadges'));
      
      if (profileRes.data) {
        setProfileForm({
          bio: profileRes.data.bio || '',
          price: profileRes.data.price?.toString() || '',
          delivery_days: profileRes.data.delivery_days?.toString() || '',
          image_url: profileRes.data.image_url || '',
          spotify_url: profileRes.data.spotify_url || '',
          payout_method: profileRes.data.payout_method || 'paypal',
          paypal_email: profileRes.data.paypal_email || '',
          genres: profileRes.data.genres || []
        });
        // Load payment data & connect status
        try {
          const [payRes, connRes] = await Promise.all([
            api.getPaymentMethods().catch(() => ({ data: {} })),
            api.getConnectStatus().catch(() => ({ data: null })),
          ]);
          setPaymentMethod(payRes.data?.preferred_method || '');
          setPaymentData(payRes.data || {});
          setConnectStatus(connRes.data);
        } catch (e) { toast.error('Failed to load payment settings'); }
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Intentionally [] — api/toast/window.location are stable refs
  const handleConnectBank = useCallback(async (isRefresh = false) => {
    setConnectLoading(true);
    try {
      const origin = window.location.origin;
      const returnUrl = `${origin}/artist-dashboard?tab=earnings&connect=return`;
      const refreshUrl = `${origin}/artist-dashboard?tab=earnings&connect=refresh`;
      const res = isRefresh
        ? await api.refreshConnectLink({ return_url: returnUrl, refresh_url: refreshUrl })
        : await api.connectBank({ return_url: returnUrl, refresh_url: refreshUrl });
      if (res.data.already_connected) {
        toast.success('Bank account already connected!');
        const statusRes = await api.getConnectStatus();
        setConnectStatus(statusRes.data);
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bank connection');
    }
    setConnectLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    // Handle Stripe Connect return
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'return') {
      api.getConnectStatus().then(res => {
        setConnectStatus(res.data);
        if (res.data.connected) toast.success('Bank account connected successfully!');
        else if (res.data.details_submitted) toast.info('Account is being verified. Check back shortly.');
        else toast.info('Onboarding incomplete. Please complete setup.');
      }).catch(() => { toast.error('Failed to check connect status'); });
      setSearchParams({ tab: 'earnings' });
    }
    if (params.get('connect') === 'refresh') {
      handleConnectBank(true);
    }
  }, [fetchData, handleConnectBank, setSearchParams]);

  useEffect(() => {
    // Update URL when tab changes
    if (activeTab !== searchParams.get('tab')) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleAcceptOrder = async (orderId) => {
    try {
      await api.acceptOrder(orderId);
      toast.success('Order accepted! You have 45 days to deliver.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept order');
    }
  };

  const handleDenyOrder = async (orderId) => {
    try {
      await api.denyOrder(orderId);
      toast.success('Order denied. Customer will be refunded.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny order');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await api.cancelOrder(orderId);
      toast.success('Order cancelled. Full refund issued to buyer.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const handleApproveCancelOrder = async (orderId) => {
    try {
      await api.approveCancelOrder(orderId);
      toast.success('Cancellation approved. Full refund issued to buyer.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve cancellation');
    }
  };

  const handleDenyCancelOrder = async (orderId) => {
    try {
      await api.denyCancelOrder(orderId);
      toast.success('Cancellation denied. Order will continue as normal.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny cancellation');
    }
  };

  const handleDeliverClick = (order) => {
    setSelectedOrder(order);
    setDeliverDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedOrder) return;

    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-m4a', 'audio/aac'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|aac)$/i)) {
      toast.error('Please upload an audio file (MP3, WAV, M4A, AAC)');
      return;
    }

    setUploadingId(selectedOrder.id);
    try {
      await api.deliverOrder(selectedOrder.id, file);
      toast.success('Dubplate delivered successfully!');
      setDeliverDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setWithdrawing(true);
    try {
      await api.requestWithdrawal(amount);
      toast.success('Withdrawal requested! We&apos;ll process it shortly.');
      setWithdrawDialogOpen(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updateData = {
        bio: profileForm.bio || undefined,
        price: profileForm.price ? parseFloat(profileForm.price) : undefined,
        delivery_days: profileForm.delivery_days ? parseInt(profileForm.delivery_days) : undefined,
        image_url: profileForm.image_url || undefined,
        spotify_url: profileForm.spotify_url || undefined,
        payout_method: profileForm.payout_method,
        paypal_email: profileForm.payout_method === 'paypal' ? profileForm.paypal_email : undefined,
        genres: profileForm.genres
      };
      
      await api.updateArtistProfile(updateData);
      toast.success('Profile updated!');
      setProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRequestVerification = async () => {
    setRequestingVerification(true);
    try {
      await api.requestVerification();
      toast.success('Verification requested! We&apos;ll review your profile.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request verification');
    } finally {
      setRequestingVerification(false);
    }
  };

  const handleDisconnectBank = async () => {
    setDisconnecting(true);
    try {
      await api.disconnectBank();
      setConnectStatus({ connected: false, status: 'not_started' });
      toast.success('Bank account disconnected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
    setDisconnecting(false);
  };

  const handleSavePaymentMethod = async () => {
    setSavingPayment(true);
    try {
      await api.updatePaymentMethods({ preferred_method: paymentMethod, ...paymentData });
      toast.success('Payment method saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
    setSavingPayment(false);
  };

  // Offer handling
  const handleAcceptOffer = async (offerId) => {
    setProcessingOfferId(offerId);
    try {
      await api.acceptOffer(offerId);
      toast.success('Offer accepted! The buyer can now complete payment.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const handleDenyOffer = async (offerId) => {
    setProcessingOfferId(offerId);
    try {
      await api.denyOffer(offerId);
      toast.success('Offer denied');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const handleOpenCounterDialog = (offer) => {
    setSelectedOffer(offer);
    // Pre-fill with a price between their offer and original
    const suggestedPrice = Math.round((offer.proposed_price + offer.original_total) / 2);
    setCounterPrice(suggestedPrice.toString());
    setCounterMessage('');
    setCounterDialogOpen(true);
  };

  const handleSubmitCounter = async () => {
    if (!selectedOffer || !counterPrice) return;
    
    const price = parseFloat(counterPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setProcessingOfferId(selectedOffer.id);
    try {
      await api.counterOffer(selectedOffer.id, price, counterMessage);
      toast.success('Counter-offer sent!');
      setCounterDialogOpen(false);
      setSelectedOffer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send counter-offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'paid');
  const activeOrders = orders.filter(o => o.status === 'accepted');
  const completedOrders = orders.filter(o => ['delivered', 'completed', 'cancelled', 'denied'].includes(o.status));
  
  // Count pending offers
  const pendingOffersCount = offers.filter(o => o.status === 'pending').length;
  const unreadNotifCount = notifications.filter(n => !n.is_read).length;

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event('refreshBadges'));
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed to mark notifications as read'); }
  };

  const handleDeleteNotification = (e, id) => {
    e.stopPropagation();
    const deleted = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    const timer = setTimeout(() => {
      api.deleteNotification(id).catch(() => {
        setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        toast.error('Failed to delete notification');
      });
    }, 5000);
    toast('Notification deleted', {
      action: { label: 'Undo', onClick: () => { clearTimeout(timer); setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); } },
      duration: 5000,
    });
  };

  const handleDeleteAllNotifications = () => {
    const backup = [...notifications];
    setNotifications([]);
    if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
    deleteTimerRef.current = setTimeout(() => {
      api.deleteAllNotifications().catch(() => { setNotifications(backup); toast.error('Failed to clear notifications'); });
    }, 5000);
    toast('All notifications cleared', {
      action: { label: 'Undo', onClick: () => { clearTimeout(deleteTimerRef.current); setNotifications(backup); } },
      duration: 5000,
    });
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      window.dispatchEvent(new Event('refreshBadges'));
    } catch { toast.error('Failed to update notification'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-3xl font-bold">Artist Dashboard</h1>
                {profile?.is_verified && (
                  <Badge className="bg-blue-500/10 text-blue-400 border-0">
                    <BadgeCheck className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              <p className="text-[#A1A1AA]">Manage your dubplate requests, offers, earnings, and profile</p>
            </div>
            <Button
              onClick={() => setProfileDialogOpen(true)}
              variant="outline"
              className="border-[#262626]"
              data-testid="edit-profile-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-surface border border-[#262626]">
              <TabsTrigger value="inbox" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-inbox">
                <InboxIcon className="w-4 h-4 mr-2" />
                Inbox
                {inboxUnread > 0 && (
                  <span className="ml-2 w-5 h-5 bg-lime text-black text-xs rounded-full flex items-center justify-center font-bold">
                    {inboxUnread}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="orders" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-orders">
                <Package className="w-4 h-4 mr-2" />
                Orders
              </TabsTrigger>
              <TabsTrigger value="offers" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-offers">
                <HandshakeIcon className="w-4 h-4 mr-2" />
                Offers
                {pendingOffersCount > 0 && (
                  <span className="ml-2 w-5 h-5 bg-lime text-black text-xs rounded-full flex items-center justify-center font-bold">
                    {pendingOffersCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="earnings" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-earnings">
                <Wallet className="w-4 h-4 mr-2" />
                Earnings
              </TabsTrigger>
              <TabsTrigger value="withdrawals" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-withdrawals">
                <ArrowDownToLine className="w-4 h-4 mr-2" />
                Withdrawals
              </TabsTrigger>
              <TabsTrigger value="exports" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-exports">
                <FolderDown className="w-4 h-4 mr-2" />
                My Exports
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-lime data-[state=active]:text-black relative" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {unreadNotifCount > 0 && (
                  <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{unreadNotifCount}</span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* INBOX TAB */}
            <TabsContent value="inbox">
              <Inbox isArtistView={true} onUnreadChange={(count) => setInboxUnread(count)} />
            </TabsContent>

            {/* ORDERS TAB */}
            <TabsContent value="orders">
              <OrdersTab orders={orders} pendingOrders={pendingOrders} activeOrders={activeOrders} completedOrders={completedOrders} statusConfig={statusConfig} handleAcceptOrder={handleAcceptOrder} handleDenyOrder={handleDenyOrder} handleDeliverClick={handleDeliverClick} handleCancelOrder={handleCancelOrder} handleApproveCancelOrder={handleApproveCancelOrder} handleDenyCancelOrder={handleDenyCancelOrder} setSelectedOrderForDispute={setSelectedOrderForDispute} setDisputeDialogOpen={setDisputeDialogOpen} />
            </TabsContent>

            {/* OFFERS TAB */}
            <TabsContent value="offers">
              <OffersTab offers={offers} offerStatusConfig={offerStatusConfig} processingOfferId={processingOfferId} handleAcceptOffer={handleAcceptOffer} handleDenyOffer={handleDenyOffer} handleOpenCounterDialog={handleOpenCounterDialog} />
            </TabsContent>

            {/* EARNINGS TAB */}
            <TabsContent value="earnings">
              <EarningsTab profile={profile} earnings={earnings} setWithdrawDialogOpen={setWithdrawDialogOpen} />
            </TabsContent>

            {/* WITHDRAWALS TAB */}
            <TabsContent value="withdrawals">
              <WithdrawalsTab profile={profile} withdrawals={withdrawals} />
            </TabsContent>

            {/* MY EXPORTS TAB */}
            <TabsContent value="exports">
              <div className="bg-surface border border-[#262626] rounded-xl p-5 max-w-2xl" data-testid="dashboard-exports-section">
                <ExportsPanel
                  exports={studioExports}
                  orders={orders}
                  onRefresh={loadStudioExports}
                  onDelivered={() => { fetchData(); loadStudioExports(); }}
                />
                <Link
                  to="/studio"
                  className="mt-4 inline-flex items-center gap-2 text-xs text-lime hover:underline"
                  data-testid="dashboard-open-studio-link"
                >
                  <Mic2 className="w-3.5 h-3.5" />Record a new session in the Studio →
                </Link>
              </div>
            </TabsContent>

            <TabsContent value="notifications">
              <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden">
                <NotificationsPanel
                  notifications={notifications}
                  handleMarkNotificationRead={handleMarkNotificationRead}
                  handleMarkAllRead={handleMarkAllRead}
                  handleDeleteNotification={handleDeleteNotification}
                  handleDeleteAllNotifications={handleDeleteAllNotifications}
                  unreadNotifCount={unreadNotifCount}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Deliver Dialog */}
      <DeliverDialog
        open={deliverDialogOpen}
        onOpenChange={setDeliverDialogOpen}
        selectedOrder={selectedOrder}
        fileInputRef={fileInputRef}
        handleFileUpload={handleFileUpload}
        uploadingId={uploadingId}
      />

      {/* Withdraw Dialog */}
      <WithdrawDialog
        open={withdrawDialogOpen}
        onOpenChange={setWithdrawDialogOpen}
        profile={profile}
        withdrawAmount={withdrawAmount}
        setWithdrawAmount={setWithdrawAmount}
        handleWithdraw={handleWithdraw}
        withdrawing={withdrawing}
      />

      {/* Counter Offer Dialog */}
      <CounterOfferDialog
        open={counterDialogOpen}
        onOpenChange={setCounterDialogOpen}
        selectedOffer={selectedOffer}
        counterPrice={counterPrice}
        setCounterPrice={setCounterPrice}
        counterMessage={counterMessage}
        setCounterMessage={setCounterMessage}
        handleSubmitCounter={handleSubmitCounter}
        processingOfferId={processingOfferId}
      />

      {/* Profile Edit Dialog */}
      <ProfileEditDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        profile={profile}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        handleSaveProfile={handleSaveProfile}
        savingProfile={savingProfile}
        handleGenreToggle={handleGenreToggle}
        handleRequestVerification={handleRequestVerification}
        requestingVerification={requestingVerification}
        connectStatus={connectStatus}
        connectLoading={connectLoading}
        handleConnectBank={handleConnectBank}
        handleDisconnectBank={handleDisconnectBank}
        disconnecting={disconnecting}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        handleSavePaymentMethod={handleSavePaymentMethod}
        savingPayment={savingPayment}
      />

      {/* Dispute Dialog */}
      <DisputeDialog
        open={disputeDialogOpen}
        onOpenChange={setDisputeDialogOpen}
        order={selectedOrderForDispute}
        onDisputeCreated={fetchData}
      />
    </div>
  );
}
